import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Grid,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";

import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import RefreshIcon from "@mui/icons-material/Refresh";

import TokenService from "../../services/TokenService";

const TokenManagement = () => {
  const [description, setDescription] = useState("");
  const [expiryDate, setExpiryDate] = useState("");

  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showAll, setShowAll] = useState(false);

  const [selectedTokenId, setSelectedTokenId] = useState(null);

  const [revokeDialog, setRevokeDialog] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const showMessage = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      severity,
      message,
    });
  };

  const loadTokens = async () => {
    try {
      setLoading(true);

      const response =
        await TokenService.getTokenList(showAll);

      setTokens(response.data.results || []);
    } catch (error) {
      showMessage(
        error?.response?.data?.error ||
          "Failed to load tokens",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTokens();
  }, [showAll]);

  const handleCreateToken = async () => {
    try {
      if (!description.trim()) {
        showMessage(
          "Please enter token description",
          "warning"
        );
        return;
      }

      const payload = {
        description,
      };

      if (expiryDate) {
        payload.expiry_date = new Date(
          expiryDate
        ).toISOString();
      }

      const response =
        await TokenService.createToken(payload);

      const token =
        response?.data?.token || "";

      navigator.clipboard.writeText(token);

      showMessage(
        "Token created and copied to clipboard"
      );

      setDescription("");
      setExpiryDate("");

      loadTokens();
    } catch (error) {
      showMessage(
        error?.response?.data?.error ||
          "Failed to create token",
        "error"
      );
    }
  };

  const handleCopy = (token) => {
    navigator.clipboard.writeText(token);

    showMessage("Token copied");
  };

  const openRevokeDialog = (id) => {
    setSelectedTokenId(id);
    setRevokeDialog(true);
  };

  const handleRevoke = async () => {
    try {
      await TokenService.revokeToken(
        selectedTokenId
      );

      showMessage(
        "Token revoked successfully"
      );

      setRevokeDialog(false);

      loadTokens();
    } catch (error) {
      showMessage(
        error?.response?.data?.error ||
          "Failed to revoke token",
        "error"
      );
    }
  };

  const columns = [
    {
      field: "id",
      headerName: "ID",
      width: 90,
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      minWidth: 250,
    },
    {
      field: "token",
      headerName: "Token",
      flex: 1.5,
      minWidth: 400,
      renderCell: (params) => (
        <Box
          display="flex"
          alignItems="center"
          width="100%"
        >
          <Typography
            variant="body2"
            noWrap
            sx={{ flexGrow: 1 }}
          >
            {params.value}
          </Typography>

          <Tooltip title="Copy Token">
            <IconButton
              size="small"
              onClick={() =>
                handleCopy(params.value)
              }
            >
              <ContentCopyIcon
                fontSize="small"
              />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: "is_active",
      headerName: "Status",
      width: 130,
      renderCell: (params) =>
        params.value ? (
          <Chip
            label="Active"
            color="success"
            size="small"
          />
        ) : (
          <Chip
            label="Revoked"
            color="error"
            size="small"
          />
        ),
    },
  {
  field: "expiry_date",
  headerName: "Expiry Date",
  width: 220,
  renderCell: ({ value }) => {
    if (!value) return "Never";

    return new Date(value).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata",
    });
  },
},
    {
      field: "created_by_name",
      headerName: "Created By",
      width: 150,
    },
    {
  field: "created_at",
  headerName: "Created At",
  width: 220,
  renderCell: ({ value }) => {
    if (!value) return "-";

    return new Date(value).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata",
    });
  },
},
    {
      field: "actions",
      headerName: "Actions",
      width: 130,
      sortable: false,
      renderCell: (params) =>
        params.row.is_active ? (
          <Tooltip title="Revoke Token">
            <IconButton
              color="error"
              onClick={() =>
                openRevokeDialog(
                  params.row.id
                )
              }
            >
              <DeleteForeverIcon />
            </IconButton>
          </Tooltip>
        ) : (
          "-"
        ),
    },
  ];

  return (
    <Box p={3}>
      <Typography
        variant="h5"
        fontWeight={700}
        mb={3}
      >
        Token Management
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Description"
              value={description}
              onChange={(e) =>
                setDescription(
                  e.target.value
                )
              }
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="datetime-local"
              label="Expiry Date"
              InputLabelProps={{
                shrink: true,
              }}
              value={expiryDate}
              onChange={(e) =>
                setExpiryDate(
                  e.target.value
                )
              }
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ height: 56 }}
              onClick={
                handleCreateToken
              }
            >
              Generate
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <FormControlLabel
            control={
              <Switch
                checked={showAll}
                onChange={(e) =>
                  setShowAll(
                    e.target.checked
                  )
                }
              />
            }
            label="Show Revoked Tokens"
          />

          <Button
            startIcon={<RefreshIcon />}
            onClick={loadTokens}
          >
            Refresh
          </Button>
        </Box>

        <DataGrid
          rows={tokens}
          columns={columns}
          autoHeight
          loading={loading}
          pageSizeOptions={[
            10,
            25,
            50,
            100,
          ]}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          disableRowSelectionOnClick
        />
      </Paper>

      <Dialog
        open={revokeDialog}
        onClose={() =>
          setRevokeDialog(false)
        }
      >
        <DialogTitle>
          Revoke Token
        </DialogTitle>

        <DialogContent>
          Are you sure you want to revoke
          this token?
          <br />
          This action takes effect
          immediately.
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() =>
              setRevokeDialog(false)
            }
          >
            Cancel
          </Button>

          <Button
            color="error"
            variant="contained"
            onClick={handleRevoke}
          >
            Revoke
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() =>
          setSnackbar({
            ...snackbar,
            open: false,
          })
        }
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TokenManagement;